import { IPackage } from "dto/IPackage";
import { ItemKind } from "dto/IKindItems";
import { IMetric } from "dto/IMetric";
import { EEvidenceEvent } from "dto/EEvidenceEvent";

/** Prefiks identyfikatorów pozycji demonstracyjnych rejestru e-doręczeń. */
export const MOCK_EDELIVERY_PREFIX = "ed-";

/** Czy dany identyfikator należy do pozycji demonstracyjnej e-doręczeń. */
export const isMockEDeliveryId = (id?: string): boolean =>
  !!id && id.startsWith(MOCK_EDELIVERY_PREFIX);

/**
 * Dane przykładowe rejestru e-doręczeń — używane jako fallback w widoku
 * `Rejestry / Doręczenia – e-doręczenia`, gdy backend nie zwraca pozycji
 * (środowisko dev / brak danych). NIE są wstrzykiwane do innych rejestrów —
 * seed dotyczy wyłącznie `ItemKind.EDeliveryMessage`.
 */
const row = (p: Partial<IPackage>): IPackage =>
  ({
    Kind: ItemKind.EDeliveryMessage,
    IsIncoming: true,
    Receivers: [],
    Attachments: [],
    Thread: null,
    Case: null,
    TagIds: [],
    StatusId: null,
    HasTasks: false,
    IsDeskActive: false,
    IsShared: false,
    IsRemoved: false,
    Unread: false,
    AttachmentsCount: 1,
    ...p,
  }) as unknown as IPackage;

export const mockEDeliveries: IPackage[] = [
  row({
    Id: "ed-0001",
    Sender: "Sąd Rejonowy dla m.st. Warszawy, I Wydział Cywilny",
    S1: "Zawiadomienie o terminie rozprawy — sygn. akt I C 1234/25",
    SendDate: new Date("2026-05-18T09:12:00") as unknown as Date,
    AvailableDate: new Date("2026-05-18T09:15:00") as unknown as Date,
    ReceiveDate: new Date("2026-05-19T08:41:00") as unknown as Date,
    ReceivedBy: "Anna Kowalska (sekretariat)",
    Pass: {
      Date: new Date("2026-05-19T10:00:00") as unknown as Date,
      From: "Sekretariat",
      To: "r.pr. Marek Wiśniewski",
    } as IPackage["Pass"],
    AttachmentsCount: 2,
  }),
  row({
    Id: "ed-0002",
    Sender: "Zakład Ubezpieczeń Społecznych, II Oddział w Warszawie",
    S1: "Decyzja w sprawie podlegania ubezpieczeniom społecznym — znak 470000/2026/0456",
    SendDate: new Date("2026-05-21T13:40:00") as unknown as Date,
    AvailableDate: new Date("2026-05-21T13:45:00") as unknown as Date,
    ReceiveDate: new Date("2026-05-22T07:55:00") as unknown as Date,
    ReceivedBy: "Anna Kowalska (sekretariat)",
    Pass: {
      Date: new Date("2026-05-22T09:30:00") as unknown as Date,
      From: "Sekretariat",
      To: "adw. Piotr Zieliński",
    } as IPackage["Pass"],
  }),
  row({
    Id: "ed-0003",
    Sender: "Urząd Skarbowy Warszawa-Mokotów",
    S1: "Wezwanie do złożenia wyjaśnień — sprawa 1471-SOR.4103.12.2026",
    SendDate: new Date("2026-05-26T11:05:00") as unknown as Date,
    AvailableDate: new Date("2026-05-26T11:08:00") as unknown as Date,
    ReceiveDate: null as unknown as Date,
    ReceivedBy: "",
    Pass: null as unknown as IPackage["Pass"],
  }),
  row({
    Id: "ed-0004",
    Sender: "Naczelny Sąd Administracyjny w Warszawie",
    S1: "Odpis postanowienia — sygn. akt II FSK 567/25",
    SendDate: new Date("2026-05-28T15:20:00") as unknown as Date,
    AvailableDate: new Date("2026-05-28T15:24:00") as unknown as Date,
    ReceiveDate: new Date("2026-05-29T09:10:00") as unknown as Date,
    ReceivedBy: "Tomasz Lewandowski",
    Pass: {
      Date: new Date("2026-05-29T11:00:00") as unknown as Date,
      From: "Tomasz Lewandowski",
      To: "r.pr. Marek Wiśniewski",
    } as IPackage["Pass"],
    AttachmentsCount: 3,
  }),
  row({
    Id: "ed-0005",
    Sender: "Sąd Rejonowy Gdańsk-Północ, VIII Wydział Gospodarczy KRS",
    S1: "Postanowienie o wpisie zmiany danych — sygn. GD.VIII NS-REJ.KRS/8421/26",
    SendDate: new Date("2026-06-02T08:30:00") as unknown as Date,
    AvailableDate: new Date("2026-06-02T08:33:00") as unknown as Date,
    ReceiveDate: new Date("2026-06-03T12:48:00") as unknown as Date,
    ReceivedBy: "Anna Kowalska (sekretariat)",
    Pass: {
      Date: new Date("2026-06-03T14:15:00") as unknown as Date,
      From: "Sekretariat",
      To: "adw. Katarzyna Nowak",
    } as IPackage["Pass"],
  }),
  row({
    Id: "ed-0006",
    Sender: "Prokuratura Rejonowa Warszawa-Śródmieście",
    S1: "Zawiadomienie o wszczęciu śledztwa — sygn. PR 2 Ds. 89.2026",
    SendDate: new Date("2026-06-05T10:00:00") as unknown as Date,
    AvailableDate: new Date("2026-06-05T10:02:00") as unknown as Date,
    ReceiveDate: null as unknown as Date,
    ReceivedBy: "",
    Pass: null as unknown as IPackage["Pass"],
    AttachmentsCount: 1,
  }),
];

// ── Metryki pozycji demonstracyjnych (odpowiednik GET /message/metric) ──────────

const FIRM_ADE = "AE:PL-41896-58723-KANCE-12";
const FIRM_NAME = "Kancelaria Prawna Poczta Prawnicza sp.k.";

interface MockMetricSpec {
  subject: string;
  senderName: string;
  senderAde: string;
  sendDate: string;
  availableDate: string;
  receiveDate: string | null;
  receivedBy: string;
  passFrom?: string;
  passTo?: string;
  passDate?: string;
}

const metricSpecs: Record<string, MockMetricSpec> = {
  "ed-0001": {
    subject: "Zawiadomienie o terminie rozprawy — sygn. akt I C 1234/25",
    senderName: "Sąd Rejonowy dla m.st. Warszawy, I Wydział Cywilny",
    senderAde: "AE:PL-10001-20002-SRWAW-01",
    sendDate: "2026-05-18T09:12:00",
    availableDate: "2026-05-18T09:15:00",
    receiveDate: "2026-05-19T08:41:00",
    receivedBy: "Anna Kowalska",
    passFrom: "Sekretariat",
    passTo: "r.pr. Marek Wiśniewski",
    passDate: "2026-05-19T10:00:00",
  },
  "ed-0002": {
    subject:
      "Decyzja w sprawie podlegania ubezpieczeniom społecznym — znak 470000/2026/0456",
    senderName: "Zakład Ubezpieczeń Społecznych, II Oddział w Warszawie",
    senderAde: "AE:PL-30003-40004-ZUSWA-02",
    sendDate: "2026-05-21T13:40:00",
    availableDate: "2026-05-21T13:45:00",
    receiveDate: "2026-05-22T07:55:00",
    receivedBy: "Anna Kowalska",
    passFrom: "Sekretariat",
    passTo: "adw. Piotr Zieliński",
    passDate: "2026-05-22T09:30:00",
  },
  "ed-0003": {
    subject: "Wezwanie do złożenia wyjaśnień — sprawa 1471-SOR.4103.12.2026",
    senderName: "Urząd Skarbowy Warszawa-Mokotów",
    senderAde: "AE:PL-50005-60006-USMOK-03",
    sendDate: "2026-05-26T11:05:00",
    availableDate: "2026-05-26T11:08:00",
    receiveDate: null,
    receivedBy: "",
  },
  "ed-0004": {
    subject: "Odpis postanowienia — sygn. akt II FSK 567/25",
    senderName: "Naczelny Sąd Administracyjny w Warszawie",
    senderAde: "AE:PL-70007-80008-NSAWA-04",
    sendDate: "2026-05-28T15:20:00",
    availableDate: "2026-05-28T15:24:00",
    receiveDate: "2026-05-29T09:10:00",
    receivedBy: "Tomasz Lewandowski",
    passFrom: "Tomasz Lewandowski",
    passTo: "r.pr. Marek Wiśniewski",
    passDate: "2026-05-29T11:00:00",
  },
  "ed-0005": {
    subject:
      "Postanowienie o wpisie zmiany danych — sygn. GD.VIII NS-REJ.KRS/8421/26",
    senderName: "Sąd Rejonowy Gdańsk-Północ, VIII Wydział Gospodarczy KRS",
    senderAde: "AE:PL-90009-10010-SRGDN-05",
    sendDate: "2026-06-02T08:30:00",
    availableDate: "2026-06-02T08:33:00",
    receiveDate: "2026-06-03T12:48:00",
    receivedBy: "Anna Kowalska",
    passFrom: "Sekretariat",
    passTo: "adw. Katarzyna Nowak",
    passDate: "2026-06-03T14:15:00",
  },
  "ed-0006": {
    subject: "Zawiadomienie o wszczęciu śledztwa — sygn. PR 2 Ds. 89.2026",
    senderName: "Prokuratura Rejonowa Warszawa-Śródmieście",
    senderAde: "AE:PL-11011-21012-PRWAR-06",
    sendDate: "2026-06-05T10:00:00",
    availableDate: "2026-06-05T10:02:00",
    receiveDate: null,
    receivedBy: "",
  },
};

const buildMetric = (id: string, s: MockMetricSpec): IMetric => {
  const received = !!s.receiveDate;

  const evidences = [
    {
      Id: `${id}-ev1`,
      ExternalId: null,
      CreationDate: s.sendDate,
      Event: EEvidenceEvent.SubmissionAcceptance,
      Reason: 0,
      Label: "Dowód wystawienia (akceptacja nadania)",
    },
    {
      Id: `${id}-ev2`,
      ExternalId: null,
      CreationDate: s.availableDate,
      Event: EEvidenceEvent.ConsignmentNotification,
      Reason: 0,
      Label: "Notyfikacja o przesyłce gotowej do odbioru",
    },
    ...(received
      ? [
          {
            Id: `${id}-ev3`,
            ExternalId: null,
            CreationDate: s.receiveDate as string,
            Event: EEvidenceEvent.ContentHandover,
            Reason: 0,
            Label: "Dowód doręczenia (dostarczenie przesyłki)",
          },
        ]
      : []),
  ];

  const history = [
    {
      Id: `${id}-h1`,
      Date: s.sendDate,
      Description: "Przesyłka wystawiona przez nadawcę",
    },
    {
      Id: `${id}-h2`,
      Date: s.availableDate,
      Description: "Przesyłka udostępniona do odbioru",
    },
    ...(received
      ? [
          {
            Id: `${id}-h3`,
            Date: s.receiveDate as string,
            Description: "Przesyłka odebrana",
            User: s.receivedBy,
          },
        ]
      : []),
  ];

  const passHistory =
    s.passFrom && s.passTo && s.passDate
      ? [
          {
            Id: 1,
            Date: s.passDate,
            From: s.passFrom,
            To: s.passTo,
          },
        ]
      : [];

  return {
    Id: id,
    ExternalId: null,
    CreationDate: s.sendDate,
    SendDate: s.sendDate,
    ReceiveDate: s.receiveDate,
    FirstNotificationDate: s.availableDate,
    SecondNotificationDate: null,
    Subject: s.subject,
    Sender: {
      ParticipantId: 1,
      UserId: "",
      Name: "",
      Surname: "",
      CompanyName: s.senderName,
      EDeliveryAddress: s.senderAde,
      ServiceProvider: null,
      Email: null,
    },
    Receivers: [
      {
        ParticipantId: 2,
        UserId: "",
        Name: "",
        Surname: "",
        CompanyName: FIRM_NAME,
        EDeliveryAddress: FIRM_ADE,
        ServiceProvider: null,
        Email: null,
        Evidences: evidences,
      },
    ],
    Attachments: [],
    Evidences: evidences,
    PassHistory: passHistory,
    ShareHistory: [],
    History: history,
    HasEpok: false,
    NewestEvidenceEvent: received
      ? EEvidenceEvent.ContentHandover
      : EEvidenceEvent.ConsignmentNotification,
  } as unknown as IMetric;
};

/** Zwraca metrykę demonstracyjną dla pozycji e-doręczeń lub `null`. */
export const getMockEDeliveryMetric = (id: string): IMetric | null => {
  const spec = metricSpecs[id];
  return spec ? buildMetric(id, spec) : null;
};

export default mockEDeliveries;
